import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Grid,
  IconButton,
  ListItemText,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import { genericSubQueriesType } from "../../../services/constants";
import MixedSqlEditor from "./MixedSqlEditor";
import { IMixedSubQueryV2 } from "./mixedQueryV2.utils";
import ViewResourceIconButton from "../../../components/ViewResourceIconButton";

// Maps the legacy Mixed Query source-type labels to the backend sub_type values.
const SOURCE_TYPE_LABELS: Record<string, string> = {
  Resource: "Resource Queries",
  External: "External Queries",
  Merged_Data: "Merged Data Queries",
  Missing_Data: "Missing Data Queries",
  Mismatched_Data: "Mismatched Data Queries",
};

// Comparison-only sub-types backed by the rule's merge results. Rendered with a
// query editor + optional Source Name + Dependencies (no resource/external fields).
const MERGE_SUB_TYPES = ["Merged_Data", "Missing_Data", "Mismatched_Data"];

export interface SubQueryCardProps {
  item: IMixedSubQueryV2;
  index: number;
  errors: any;
  isReadOnly?: boolean;
  // Data lists supplied by the container (ported from the legacy tab).
  resourcesData: any[];
  linkedServicesData: any[];
  connectionKeys: any[];
  // Sub-query type options offered in the type dropdown. Defaults to the generic
  // 2-type list; the Comparison flow passes the 5-type ruleSubQueriesType list.
  availableSubTypes?: string[];
  // Handlers ported from the legacy GenericMixedQueryTab.
  onTypeChange: (index: number, value: string | null) => void;
  onFieldChange: (index: number, field: string, value: any) => void;
  onLinkedServiceChange: (index: number, field: string, value: any) => void;
  onQueryChange: (index: number, value: string) => void;
  onEditorFocus: (index: number) => void;
  onEditorBlur: () => void;
  onFetchColumns: (index: number, itemId: number) => void;
  onRemove: (index: number) => void;
  // Toggles the sub-query's active/inactive state. Inactive sources are removed
  // from other sub-queries' dependency pools (handled by the container).
  onToggleActive?: (index: number) => void;
  // Optional: opens the focused full-screen editor for this source.
  onOpenEditor?: (index: number) => void;
  // Optional: opens the standalone pop-out query editor dialog for this source.
  onPopOut?: (index: number) => void;
  // Dependencies (sub-queries this one may reference).
  getDependencyDisplayLabel: (marker: string) => string;
  fetchColumnsLoading?: boolean;
  // External editor ref so the container can insert columns/variables at the
  // cursor of this sub-query's editor.
  editorRef?: React.MutableRefObject<any>;
  // Presentational slots for reused column chips.
  columnsChipsSlot?: React.ReactNode;
  // Presentational slot for the reused Available Variables panel.
  variablesSlot?: React.ReactNode;
  referenceLabel: string;
  // "card" (compact grid) stacks the editor and chips; "expanded" (full-screen
  // editor) shows the Available Columns/Variables side-by-side with the editor.
  layout?: "card" | "expanded";
}

// Restyled data-source card (Figma "Query with Preview Expanded"). It reuses the
// exact legacy field contract; only the layout is changed. All state is owned by
// the container so the proven handler logic stays intact.
const SubQueryCard: React.FC<SubQueryCardProps> = ({
  item,
  index,
  errors,
  isReadOnly = false,
  resourcesData,
  linkedServicesData,
  connectionKeys,
  availableSubTypes = genericSubQueriesType,
  onTypeChange,
  onFieldChange,
  onLinkedServiceChange,
  onQueryChange,
  onEditorFocus,
  onEditorBlur,
  onFetchColumns,
  onRemove,
  onToggleActive,
  onOpenEditor,
  onPopOut,
  getDependencyDisplayLabel,
  fetchColumnsLoading = false,
  editorRef,
  columnsChipsSlot,
  variablesSlot,
  referenceLabel,
  layout = "card",
}) => {
  const err = errors?.[index] || {};
  const linkedServices = (linkedServicesData || []).filter(
    (service: any) => service?.type === "sql",
  );
  const linkedServiceValue =
    linkedServices.find((s: any) => s.id === item?.linked_service_id) || null;
  const connectionKeyValue =
    (connectionKeys || []).find((k: any) => k.id === item?.connection_key_id) ||
    null;
  const resourceValue =
    (resourcesData || []).find((r: any) => r.id === item?.resource_id) || null;

  const handleCopyReference = () => {
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(`<${referenceLabel}>`);
    }
  };

  // Dependencies field — sub-queries this one may reference. Rendered inline in
  // the selector grid (after Table Name for External, after Source Name for
  // Resource). Every sub-query may reference any other active sub-query,
  // including sub-queries that appear later in the editor.
  const dependenciesGridItem = (item.available_dependencies?.length || 0) >
    0 && (
    <Grid item xl={6} lg={6} md={12} sm>
      <Autocomplete
        fullWidth
        multiple
        disabled={isReadOnly}
        options={item.available_dependencies || []}
        getOptionLabel={(option: any) => getDependencyDisplayLabel(option)}
        value={item?.dependency_list || []}
        onChange={(_e, value) => onFieldChange(index, "dependency_list", value)}
        renderTags={(value, getTagProps) => (
          <div className="align-from-left">
            {value.map((option: any, i: number) => (
              <Chip
                {...getTagProps({ index: i })}
                key={i}
                label={getDependencyDisplayLabel(option)}
              />
            ))}
          </div>
        )}
        renderOption={(props, option: any, { selected }) => (
          <MenuItem
            {...props}
            key={`${option}`}
            value={option}
            sx={{ justifyContent: "space-between" }}
          >
            {getDependencyDisplayLabel(option)}
            {selected ? <CheckCircleRounded color="info" /> : null}
          </MenuItem>
        )}
        renderInput={(params) => (
          <div className="autocomplete-chips-direction">
            <TextField
              {...params}
              name="dependency_list"
              label={<span>Dependencies</span>}
              placeholder="Select..."
              InputLabelProps={{ shrink: true }}
            />
          </div>
        )}
        className="form-control-autocomplete"
      />
    </Grid>
  );

  return (
    <Box
      className={`mixed-v2-source-card ${
        layout === "card" && item?.sub_type === "External"
          ? "mixed-v2-source-card--external"
          : ""
      } ${item?.is_active === false ? "mixed-v2-source-card--inactive" : ""}`}
    >
      {/* Header: reference name, source badge, delete */}
      <Box className="mixed-v2-source-card__header">
        <Box className="mixed-v2-source-card__title">
          <Typography
            className="mixed-v2-source-card__name"
            title={referenceLabel}
          >
            {referenceLabel}
          </Typography>
          <Chip
            size="small"
            className="mixed-v2-source-card__badge"
            label={`Source ${index + 1}`}
          />
        </Box>
        <Box className="mixed-v2-source-card__header-actions">
          {onToggleActive && (
            <Tooltip
              title={
                item?.is_active === false
                  ? "Activate this data source"
                  : "Deactivate this data source"
              }
              placement="top"
              arrow
            >
              <Box className="mixed-v2-source-card__active-toggle">
                <Typography component="span" variant="caption">
                  Active
                </Typography>
                <Switch
                  size="small"
                  checked={item?.is_active !== false}
                  disabled={isReadOnly}
                  onChange={() => onToggleActive(index)}
                />
              </Box>
            </Tooltip>
          )}
          {!isReadOnly && (
            <Tooltip title="Remove data source" placement="top" arrow>
              <IconButton
                size="small"
                className="mixed-v2-source-card__close"
                onClick={() => onRemove(index)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Source type + Resource/External selectors — responsive MUI Grid
          matching the legacy tab breakpoints (xl/lg = 6, md = 12). */}
      <Box className="mixed-v2-source-card__selectors">
        <Grid container columnSpacing={1.5} rowSpacing={2.5}>
          <Grid item xl={6} lg={6} md={12} sm>
            <Autocomplete
              fullWidth
              disabled={isReadOnly}
              options={availableSubTypes ?? []}
              getOptionLabel={(option: any) =>
                SOURCE_TYPE_LABELS[option] || option || ""
              }
              value={
                (availableSubTypes || []).find(
                  (o: string) => o === item?.sub_type,
                ) || null
              }
              onChange={(_e, value) => onTypeChange(index, value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={<span>{`Select Sub Query ${index + 1}`}</span>}
                  placeholder="Select..."
                  InputLabelProps={{ shrink: true }}
                  className="form-control-autocomplete"
                />
              )}
              className="form-control-autocomplete"
            />
          </Grid>

          {item?.sub_type === "Resource" && (
            <Grid item xl={6} lg={6} md={12} sm>
              <Box className="d-flex align-items-end cols-gap-6">
                <Autocomplete
                  fullWidth
                  disabled={isReadOnly}
                  options={resourcesData ?? []}
                  getOptionLabel={(option: any) => option.resource_name || ""}
                  value={resourceValue}
                  onChange={(_e, value) =>
                    onFieldChange(index, "resource_id", value)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="resource_id"
                      label={<span>Select Resource</span>}
                      placeholder="Select..."
                      InputLabelProps={{ shrink: true }}
                      error={!!err?.resource_id}
                      helperText={err?.resource_id || ""}
                      className={`form-control-autocomplete ${
                        err?.resource_id ? "has-error" : ""
                      }`}
                    />
                  )}
                  renderOption={(params: any, opt: any) => (
                    <li {...params} key={opt.id}>
                      <ListItemText>{opt.resource_name}</ListItemText>
                    </li>
                  )}
                  className="form-control-autocomplete"
                />
                {item?.resource_id && (
                  <ViewResourceIconButton
                    resourceId={item.resource_id}
                    tooltipText="Click to view resource"
                  />
                )}
              </Box>
            </Grid>
          )}

          {item?.sub_type === "External" && (
            <>
              <Grid item xl={6} lg={6} md={12} sm>
                <Autocomplete
                  fullWidth
                  disabled={isReadOnly}
                  options={linkedServices ?? []}
                  getOptionLabel={(option: any) => option.name || ""}
                  value={linkedServiceValue}
                  onChange={(_e, value) =>
                    onLinkedServiceChange(index, "linked_service_id", value)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="linked_service"
                      label={<span>Connection Type</span>}
                      placeholder="Select..."
                      InputLabelProps={{ shrink: true }}
                      error={!!err?.linked_service_id}
                      helperText={err?.linked_service_id || ""}
                      className={`form-control-autocomplete ${
                        err?.linked_service_id ? "has-error" : ""
                      }`}
                    />
                  )}
                  renderOption={(params: any, opt: any) => (
                    <li {...params} key={opt.id}>
                      <ListItemText>{opt.name}</ListItemText>
                    </li>
                  )}
                  className="form-control-autocomplete"
                />
              </Grid>
              <Grid item xl={6} lg={6} md={12} sm>
                <Autocomplete
                  fullWidth
                  disabled={isReadOnly}
                  options={connectionKeys ?? []}
                  getOptionLabel={(opt: any) => opt.name || ""}
                  value={connectionKeyValue}
                  onChange={(_e, value) =>
                    onFieldChange(index, "connection_key_id", value)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name="connection_key"
                      label={<span>Connection Key</span>}
                      placeholder="Select..."
                      InputLabelProps={{ shrink: true }}
                      error={!!err?.connection_key_id}
                      helperText={err?.connection_key_id || ""}
                      className={`form-control-autocomplete ${
                        err?.connection_key_id ? "has-error" : ""
                      }`}
                    />
                  )}
                  className="form-control-autocomplete"
                />
              </Grid>
              <Grid item xl={6} lg={6} md={12} sm>
                <TextField
                  name="external_source_name"
                  disabled={isReadOnly}
                  label={
                    <span>
                      External Source Name
                      <span className="required-asterisk">*</span>
                    </span>
                  }
                  fullWidth
                  variant="outlined"
                  value={item?.external_source_name || ""}
                  onChange={(e) =>
                    onFieldChange(index, "external_source_name", e.target.value)
                  }
                  error={!!err?.external_source_name}
                  helperText={err?.external_source_name || ""}
                  className={`form-control-autocomplete ${
                    err?.external_source_name ? "has-error" : ""
                  }`}
                />
              </Grid>
              <Grid item xl={6} lg={6} md={12} sm>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                  }}
                >
                  <TextField
                    name="table_name"
                    disabled={isReadOnly}
                    label={<span>Table Name</span>}
                    fullWidth
                    variant="outlined"
                    value={item?.table_name || ""}
                    onChange={(e) =>
                      onFieldChange(index, "table_name", e.target.value)
                    }
                    error={!!err?.table_name}
                    helperText={err?.table_name || ""}
                    className={`form-control-autocomplete ${
                      err?.table_name ? "has-error" : ""
                    }`}
                  />
                  <Button
                    color="secondary"
                    variant="contained"
                    className="btn-orange btn-blue"
                    onClick={() => onFetchColumns(index, item.id)}
                    disabled={
                      isReadOnly ||
                      fetchColumnsLoading ||
                      !(
                        item?.connection_key_id &&
                        item?.linked_service_id &&
                        item?.table_name
                      )
                    }
                    sx={{
                      height: 56,
                      mt: "24px",
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {fetchColumnsLoading ? "Fetching..." : "fetch columns"}
                  </Button>
                </Box>
              </Grid>
              {dependenciesGridItem}
              <Grid item xl={12} lg={12} md={12} sm={12}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  <label className="base-resource-checkbox mt-16">
                    <Checkbox
                      disabled={isReadOnly}
                      checked={item?.create_temp_table_index || false}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        onFieldChange(
                          index,
                          "create_temp_table_index",
                          event.target.checked,
                        )
                      }
                      sx={{ "&.Mui-checked": { color: "#537ea0" } }}
                    />
                    <span>Create index on temporary tables</span>
                  </label>
                  <label className="base-resource-checkbox mt-16">
                    <Checkbox
                      disabled={isReadOnly}
                      checked={item?.create_permanent_table || false}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        onFieldChange(
                          index,
                          "create_permanent_table",
                          event.target.checked,
                        )
                      }
                      sx={{ "&.Mui-checked": { color: "#537ea0" } }}
                    />
                    <span>Create permanent table</span>
                  </label>
                </Box>
              </Grid>
            </>
          )}

          {item?.sub_type === "Resource" && (
            <Grid item xl={6} lg={6} md={12} sm>
              <TextField
                name="external_source_name"
                disabled={isReadOnly}
                label={<span>Source Name</span>}
                fullWidth
                variant="outlined"
                value={item?.external_source_name || ""}
                onChange={(e) =>
                  onFieldChange(index, "external_source_name", e.target.value)
                }
                className="form-control-autocomplete"
              />
            </Grid>
          )}
          {item?.sub_type === "Resource" && dependenciesGridItem}

          {/* Comparison sub-types (Merged/Missing/Mismatched) carry no resource
              or external fields — only an optional Source Name and Dependencies. */}
          {item?.sub_type && MERGE_SUB_TYPES.includes(item.sub_type) && (
            <Grid item xl={6} lg={6} md={12} sm>
              <TextField
                name="external_source_name"
                disabled={isReadOnly}
                label={<span>Source Name</span>}
                fullWidth
                variant="outlined"
                value={item?.external_source_name || ""}
                onChange={(e) =>
                  onFieldChange(index, "external_source_name", e.target.value)
                }
                className="form-control-autocomplete"
              />
            </Grid>
          )}
          {item?.sub_type &&
            MERGE_SUB_TYPES.includes(item.sub_type) &&
            dependenciesGridItem}
        </Grid>
      </Box>

      {/* SQL editor + Available Columns/Variables. In the expanded layout the
          editor and the chips sit side-by-side; in the card layout they stack. */}
      {item?.sub_type && (
        <Box
          className={`mixed-v2-source-card__editor-row ${
            layout === "expanded" ? "is-expanded" : "is-card"
          }`}
        >
          <Box className="mixed-v2-source-card__editor">
            <MixedSqlEditor
              value={item?.data_pull_query || ""}
              onChange={(value: string) => onQueryChange(index, value)}
              editorStateKey={`mixed-v2-subquery-${item.id}`}
              label={
                item?.sub_type === "External"
                  ? "External Query"
                  : item?.sub_type === "Merged_Data"
                    ? "Merged Query"
                    : item?.sub_type === "Missing_Data"
                      ? "Missing Query"
                      : item?.sub_type === "Mismatched_Data"
                        ? "Mismatched Query"
                        : "Resource Query"
              }
              error={err}
              setErrors={() => null}
              onFocus={() => onEditorFocus(index)}
              onBlur={onEditorBlur}
              showAvailableVariables={false}
              showResourceColumnsChips={false}
              editorRef={editorRef}
              onPopOut={onPopOut ? () => onPopOut(index) : undefined}
            />
          </Box>

          {(variablesSlot || columnsChipsSlot) && (
            <Box className="mixed-v2-source-card__side-panel">
              {variablesSlot && (
                <Box className="mixed-v2-source-card__variables">
                  {variablesSlot}
                </Box>
              )}
              {columnsChipsSlot && (
                <Box className="mixed-v2-source-card__columns">
                  {columnsChipsSlot}
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Reference footer */}
      <Box className="mixed-v2-source-card__footer">
        <Typography className="mixed-v2-source-card__reference-label">
          Reference in report as:
        </Typography>
        <Chip
          size="small"
          className="mixed-v2-source-card__reference-chip"
          label={referenceLabel}
        />
        <Tooltip title="Copy reference" placement="top" arrow>
          <IconButton
            size="small"
            className="mixed-v2-source-card__copy"
            onClick={handleCopyReference}
          >
            <ContentCopyIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default SubQueryCard;
